import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Key, Copy, RefreshCw, Loader2, ShieldCheck, Code,
  CheckCircle2, AlertTriangle, Trash2, Plug
} from "lucide-react";

const APIPage = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [keyInfo, setKeyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [newKey, setNewKey] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/pos/api-key`, { headers });
        setKeyInfo(res.data);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    load();
  }, [token]);

  const generateKey = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/pos/api-key/generate`, {}, { headers });
      setNewKey(res.data.api_key);
      setKeyInfo({ has_key: true, masked_key: res.data.api_key.slice(0, 12) + "..." + res.data.api_key.slice(-4), active: true });
      toast.success("Clé API générée !");
    } catch { toast.error("Erreur de génération"); }
    finally { setGenerating(false); }
  };

  const revokeKey = async () => {
    try {
      await axios.delete(`${API}/pos/api-key`, { headers });
      setKeyInfo({ has_key: false });
      setNewKey(null);
      toast.success("Clé révoquée");
    } catch { toast.error("Erreur"); }
  };

  const copyKey = () => {
    if (newKey) { navigator.clipboard.writeText(newKey); toast.success("Clé copiée !"); }
  };

  const baseUrl = process.env.REACT_APP_BACKEND_URL + "/api/pos";

  return (
    <Layout>
      <div className="p-6 md:p-8 space-y-6" data-testid="api-page">
        <div>
          <h1 className="text-3xl font-outfit font-bold text-white flex items-center gap-3">
            <Plug className="w-8 h-8 text-sky-400" />
            API Logiciels de Caisse
          </h1>
          <p className="text-slate-400 mt-1">Connectez MAGIC ACHAT, MEDICIEL ou tout autre logiciel à CMES INTEL-STOCK</p>
        </div>

        {/* API Key Management */}
        <Card className="glass-card border border-sky-500/30" data-testid="api-key-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-white flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-400" /> Clé API
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading ? (
              <Loader2 className="w-6 h-6 text-sky-500 animate-spin" />
            ) : keyInfo?.has_key ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Badge className={keyInfo.active ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/100/20 text-red-400"}>
                    {keyInfo.active ? "Active" : "Révoquée"}
                  </Badge>
                  <code className="text-sky-400 bg-slate-900 px-3 py-1.5 rounded font-mono text-sm">{keyInfo.masked_key}</code>
                </div>
                {newKey && (
                  <div className="bg-amber-500/100/10 border border-amber-500/30 rounded-lg p-3">
                    <p className="text-amber-400 text-xs mb-2 flex items-center gap-1">
                      <AlertTriangle className="w-3.5 h-3.5" /> Copiez cette clé maintenant, elle ne sera plus affichée :
                    </p>
                    <div className="flex items-center gap-2">
                      <code className="text-white bg-slate-950 px-3 py-2 rounded font-mono text-sm flex-1 break-all" data-testid="api-key-value">{newKey}</code>
                      <Button size="sm" onClick={copyKey} className="bg-sky-600 text-white" data-testid="copy-api-key">
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button onClick={generateKey} disabled={generating} variant="outline" className="border-white/10 text-slate-300"
                    data-testid="regenerate-key-btn">
                    <RefreshCw className="w-4 h-4 mr-2" /> Régénérer
                  </Button>
                  <Button onClick={revokeKey} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/100/100/10"
                    data-testid="revoke-key-btn">
                    <Trash2 className="w-4 h-4 mr-2" /> Révoquer
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <Key className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400 mb-3">Aucune clé API. Générez-en une pour connecter votre logiciel.</p>
                <Button onClick={generateKey} disabled={generating} className="bg-sky-600 hover:bg-sky-700 text-white"
                  data-testid="generate-key-btn">
                  {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Key className="w-4 h-4 mr-2" />}
                  Générer ma clé API
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Documentation */}
        <Card className="glass-card" data-testid="api-docs-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-white flex items-center gap-2">
              <Code className="w-5 h-5 text-emerald-400" /> Documentation API
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-slate-400 text-sm">
              Toutes les requêtes nécessitent le header <code className="text-sky-400 bg-slate-900 px-1.5 py-0.5 rounded text-xs">X-API-Key</code> avec votre clé API.
            </p>

            {[
              { method: "GET", path: "/pos/status", desc: "Vérifier la connexion", body: null,
                response: '{ "connected": true, "pharmacy": "Ma Pharmacie" }' },
              { method: "GET", path: "/pos/products", desc: "Récupérer l'inventaire", body: null,
                response: '{ "count": 50, "products": [...] }' },
              { method: "POST", path: "/pos/products/sync", desc: "Synchroniser des produits",
                body: `[{
  "name": "Paracétamol 500mg",
  "category": "Antalgique",
  "batch_number": "LOT-001",
  "expiry_date": "2027-06-15",
  "quantity": 200,
  "unit_price": 500,
  "supplier": "Fournisseur"
}]`,
                response: '{ "success": true, "imported": 1 }' },
              { method: "POST", path: "/pos/sales/sync", desc: "Envoyer les ventes pour analyse",
                body: `[{
  "product_name": "Paracétamol 500mg",
  "category": "Antalgique",
  "quantity_sold": 10,
  "unit_price": 500,
  "sale_date": "2026-03-05",
  "payment_method": "cash"
}]`,
                response: '{ "success": true, "imported": 1 }' },
              { method: "GET", path: "/pos/sales?start_date=2026-01-01&end_date=2026-03-31", desc: "Récupérer les ventes (avec filtres)",
                body: null,
                response: '{ "count": 150, "total_revenue": 850000, "top_products": [...] }' },
              { method: "GET", path: "/pos/sales/summary", desc: "Résumé mensuel des ventes", body: null,
                response: '{ "monthly_summary": [{"month":"2026-01", "revenue": 450000}] }' },
              { method: "POST", path: "/pos/destructions", desc: "Demander une destruction",
                body: '{ "product_ids": ["id1", "id2"], "reason": "Périmés", "priority": "normal" }',
                response: '{ "success": true, "destruction_id": "..." }' },
              { method: "GET", path: "/pos/destructions", desc: "Consulter les destructions", body: null,
                response: '{ "count": 5, "destructions": [...] }' },
            ].map((ep) => (
              <div key={ep.path + ep.method} className="border border-white/5 rounded-lg overflow-hidden">
                <div className="flex items-center gap-3 p-3 bg-slate-800/50">
                  <Badge className={ep.method === "GET" ? "bg-emerald-500/20 text-emerald-400" : "bg-sky-500/20 text-sky-400"}>
                    {ep.method}
                  </Badge>
                  <code className="text-white text-sm font-mono">{ep.path}</code>
                  <span className="text-slate-500 text-xs ml-auto">{ep.desc}</span>
                </div>
                {ep.body && (
                  <div className="p-3 border-t border-white/5">
                    <p className="text-slate-500 text-[10px] mb-1">Body :</p>
                    <pre className="text-emerald-300 text-xs bg-slate-950 p-2 rounded overflow-x-auto">{ep.body}</pre>
                  </div>
                )}
                <div className="p-3 border-t border-white/5">
                  <p className="text-slate-500 text-[10px] mb-1">Réponse :</p>
                  <pre className="text-sky-300 text-xs bg-slate-950 p-2 rounded overflow-x-auto">{ep.response}</pre>
                </div>
              </div>
            ))}

            <div className="bg-slate-800/50 rounded-lg p-4 border border-white/5">
              <p className="text-slate-300 text-sm font-semibold mb-2 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-emerald-400" /> Exemple d'intégration
              </p>
              <pre className="text-slate-300 text-xs bg-slate-950 p-3 rounded overflow-x-auto whitespace-pre">{`# Python (MAGIC ACHAT / MEDICIEL)
import requests

API_URL = "${baseUrl}"
API_KEY = "votre_cle_api"
HEADERS = {"X-API-Key": API_KEY, "Content-Type": "application/json"}

# Vérifier la connexion
r = requests.get(f"{API_URL}/status", headers=HEADERS)
print(r.json())

# Synchroniser un produit
products = [{"name": "Doliprane", "category": "Antalgique",
  "batch_number": "LOT-001", "expiry_date": "2027-12-31",
  "quantity": 100, "unit_price": 750}]
r = requests.post(f"{API_URL}/products/sync", json=products, headers=HEADERS)
print(r.json())

# Envoyer les ventes du jour pour analyse
sales = [{"product_name": "Doliprane", "category": "Antalgique",
  "quantity_sold": 5, "unit_price": 750,
  "sale_date": "2026-03-05", "payment_method": "cash"}]
r = requests.post(f"{API_URL}/sales/sync", json=sales, headers=HEADERS)
print(r.json())

# Récupérer l'analyse des ventes (période)
r = requests.get(f"{API_URL}/sales?start_date=2026-01-01&end_date=2026-03-31",
  headers=HEADERS)
print(r.json())  # total_revenue, top_products, etc.`}</pre>
            </div>
          </CardContent>
        </Card>

        {/* Security */}
        <Card className="glass-card">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <ShieldCheck className="w-10 h-10 text-emerald-500 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-white">Sécurité de l'API</h3>
                <ul className="text-sm text-slate-400 mt-2 space-y-1">
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Authentification par clé API unique par pharmacie</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Communications chiffrées HTTPS</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Données isolées : chaque pharmacie ne voit que ses propres données</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Clé révocable à tout moment</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Journalisation de toutes les requêtes API</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default APIPage;
